/**
 * Lusty Chic Size Engine — deterministic UK jeans size recommendation (post-V1 extension).
 * Namespace: window.LustyChicFit.SizeEngine (LustyChicFit reserved in Phase 1 / CLAUDE.md).
 *
 * Fully separate from assets/lc-fit-engine.js: this file never reads, writes, or
 * influences displayedScore, rawCompatibility, completeness, category scoring, or
 * rankProducts(). A product can independently carry an 84% Fit Match and a "size up
 * one" recommendation — the two systems never interact (Architecture §C.1 / merchant
 * guide §3, "Recommended Size Is Not the Same as Fit Match").
 *
 * No AI, no randomness, no network calls. Pure functions only, except where noted.
 *
 * Expected profile shape (superset of assets/lc-fit-quiz.js's emptyProfile(), V2):
 *   { ...V1 fields unchanged..., usualUkSize: number|undefined }
 * A V1 profile with no usualUkSize is valid input — recommendSize() returns
 * status "missing-customer-size" rather than throwing or guessing.
 *
 * Expected product shape (reuses the exact field names already embedded by
 * sections/lc-fit-results.liquid and snippets/lc-fit-product-match.liquid — no
 * parallel/duplicate fields invented):
 *   { title, legFit: string[], waistRise: string|null,
 *     sizes: [{ size: number, exists: true, available: bool }, ...] }
 * `sizes` only ever contains entries the product genuinely offers — every entry's
 * `exists` is true by construction (it was built by iterating the product's real
 * Shopify variants), so "no entry for size N" already means "doesn't exist."
 */
window.LustyChicFit = window.LustyChicFit || {};

(function (ns) {
  'use strict';

  var UK_SIZE_ORDER = [6, 8, 10, 12, 14, 16, 18, 20];

  function isNonEmptyString(v) {
    return typeof v === 'string' && v.trim() !== '';
  }

  function sizeIndex(size) {
    return UK_SIZE_ORDER.indexOf(size);
  }

  function sizeUp(size) {
    var i = sizeIndex(size);
    if (i === -1 || i === UK_SIZE_ORDER.length - 1) return null;
    return UK_SIZE_ORDER[i + 1];
  }

  function sizeDown(size) {
    var i = sizeIndex(size);
    if (i <= 0) return null;
    return UK_SIZE_ORDER[i - 1];
  }

  function normalizeTitle(title) {
    return isNonEmptyString(title) ? title.toLowerCase() : '';
  }

  function detectPlusSize(product) {
    var sizes = (product && product.sizes) || [];
    for (var i = 0; i < sizes.length; i++) {
      if (sizes[i] && sizes[i].size === 20) return true;
    }
    return false;
  }

  var COATED_PATTERN = /\bcoated\b|\bpu\b|\bfaux\b|\bleather[\s-]look\b/;
  function detectCoated(title) {
    return COATED_PATTERN.test(normalizeTitle(title));
  }

  var SUPER_PATTERN = /\bsuper\b/;
  function detectSuper(title) {
    return SUPER_PATTERN.test(normalizeTitle(title));
  }

  function detectSkinny(product) {
    var fits = (product && product.legFit) || [];
    for (var i = 0; i < fits.length; i++) {
      if (isNonEmptyString(fits[i]) && fits[i].toLowerCase() === 'skinny') return true;
    }
    return false;
  }

  function detectLowRise(product) {
    return !!(product && isNonEmptyString(product.waistRise) && product.waistRise.toLowerCase() === 'low');
  }

  function getProductFlags(product) {
    return {
      plusSize: detectPlusSize(product),
      coated: detectCoated(product && product.title),
      superHighWaist: detectSuper(product && product.title),
      skinny: detectSkinny(product),
      lowRise: detectLowRise(product)
    };
  }

  function findSizeEntry(product, size) {
    var sizes = (product && product.sizes) || [];
    for (var i = 0; i < sizes.length; i++) {
      if (sizes[i] && sizes[i].size === size) return sizes[i];
    }
    return null;
  }

  function sizeExists(product, size) {
    return findSizeEntry(product, size) !== null;
  }

  function lowerBoundaryShapeNote(shape) {
    if (shape === 'Pear' || shape === 'Hourglass' || shape === 'Apple') {
      return 'This style is particularly well suited to your proportions at the lower end of this range.';
    }
    if (shape === 'Rectangle' || shape === 'Inverted Triangle') {
      return 'Fit at the lower end of this range may depend more on your preferred silhouette.';
    }
    return '';
  }

  function applyRules(profile, product, flags, usual) {
    var shape = profile.body_shape_unsure ? null : profile.body_shape;
    var height = profile.height;

    if (usual === 18) {
      if (shape === 'Hourglass') {
        return {
          size: 18,
          rule: 'uk18-hourglass',
          direction: 'true-to-size',
          reason: 'For an Hourglass profile at UK18, we recommend staying with your usual size.'
        };
      }
      return {
        size: 20,
        rule: 'uk18-size-up',
        direction: 'size-up',
        reason: 'Based on our fit experience at UK18, we recommend going one size up in this style.'
      };
    }

    if (flags.plusSize && usual === 12) {
      return {
        size: 10,
        rule: 'plus-size-12-to-10',
        direction: 'size-down',
        reason: 'This Plus Size style tends to fit more generously around UK10–12, so we suggest sizing down one size.'
      };
    }
    if (flags.plusSize && usual === 10) {
      if (sizeExists(product, 8)) {
        return {
          size: 8,
          rule: 'plus-size-10-to-8',
          direction: 'size-down',
          reason: 'This Plus Size style offers a smaller size, so we suggest sizing down to UK8.'
        };
      }
      var note = lowerBoundaryShapeNote(shape);
      return {
        size: 10,
        rule: 'plus-size-10-floor',
        direction: 'true-to-size',
        reason: ('UK10 is the smallest size offered in this Plus Size style.' + (note ? ' ' + note : '')).trim()
      };
    }

    if (!flags.plusSize && height === 'tall' && usual === 10 && flags.lowRise) {
      var tallMap = { Apple: 12, Pear: 12, Rectangle: 12, Hourglass: 10, 'Inverted Triangle': 10 };
      if (shape && Object.prototype.hasOwnProperty.call(tallMap, shape)) {
        var tallSize = tallMap[shape];
        return {
          size: tallSize,
          rule: 'tall-low-rise-10',
          direction: tallSize === 10 ? 'true-to-size' : 'size-up',
          reason: tallSize === 10
            ? 'This low-rise style fits true to size for your profile.'
            : 'For tall customers in this low-rise style, we recommend sizing up from UK10 for a more comfortable fit.'
        };
      }
    }

    if (!flags.plusSize && flags.skinny && flags.coated && usual >= 12) {
      var up4 = sizeUp(usual);
      if (up4 !== null) {
        return {
          size: up4,
          rule: 'coated-skinny-size-up',
          direction: 'size-up',
          reason: 'This coated skinny style tends to fit smaller from UK12 upwards, so we suggest one size up.'
        };
      }
    }

    if (!flags.plusSize && flags.superHighWaist && usual >= 12) {
      var up5 = sizeUp(usual);
      if (up5 !== null) {
        return {
          size: up5,
          rule: 'super-high-waist-size-up',
          direction: 'size-up',
          reason: 'Our Super High Waist styles tend to fit smaller from UK12 upwards, so we suggest one size up.'
        };
      }
    }

    return null;
  }

  function recommendSize(profile, product) {
    try {
      if (!profile || typeof profile !== 'object') {
        return blankResult('insufficient-product-data', null);
      }

      if (typeof profile.usualUkSize !== 'number' || !isFinite(profile.usualUkSize)) {
        var flagsNoSize = getProductFlags(product);
        return {
          status: 'missing-customer-size',
          usualSize: null,
          recommendedSize: null,
          direction: null,
          rule: null,
          reason: 'Add your usual UK jeans size to your Fit Profile for a personalised size suggestion.',
          variantExists: null,
          available: null,
          productFlags: flagsNoSize
        };
      }

      var usual = profile.usualUkSize;
      var flags = getProductFlags(product);

      var rec = applyRules(profile, product, flags, usual);
      if (!rec) {
        rec = {
          size: usual,
          rule: 'default-usual-size',
          direction: 'true-to-size',
          reason: 'We suggest starting with your usual UK size for this style.'
        };
      }

      var entry = findSizeEntry(product, rec.size);
      var result = {
        status: null,
        usualSize: usual,
        recommendedSize: rec.size,
        direction: rec.direction,
        rule: rec.rule,
        reason: rec.reason,
        variantExists: entry !== null,
        available: entry ? !!entry.available : false,
        productFlags: flags
      };

      if (!entry) {
        result.status = 'recommended-size-not-offered';
        result.reason = "Your suggested size would be UK" + rec.size + ", but this style isn't offered in that size.";
      } else if (!entry.available) {
        result.status = 'recommended-size-sold-out';
      } else if (rec.direction === 'true-to-size') {
        result.status = 'true-to-size';
      } else {
        result.status = 'recommended';
      }

      return result;
    } catch (e) {
      return blankResult('insufficient-product-data', profile && typeof profile.usualUkSize === 'number' ? profile.usualUkSize : null);
    }
  }

  function blankResult(status, usualSize) {
    return {
      status: status,
      usualSize: usualSize,
      recommendedSize: null,
      direction: null,
      rule: null,
      reason: null,
      variantExists: null,
      available: null,
      productFlags: null
    };
  }

  ns.SizeEngine = {
    UK_SIZE_ORDER: UK_SIZE_ORDER,
    sizeUp: sizeUp,
    sizeDown: sizeDown,
    getProductFlags: getProductFlags,
    recommendSize: recommendSize
  };
})(window.LustyChicFit);
